import React, { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ChevronDown, ChevronRight, Download, FileSpreadsheet, FileText, Users, BarChart3, Award, Calendar, TrendingUp, Filter, Trophy } from "lucide-react";
import iconPresentActive from "@/assets/attendance/anwesend_aktiv.png";
import iconAbsentActive from "@/assets/attendance/fehlt_aktiv.png";
import iconExcusedActive from "@/assets/attendance/entschuldigt_aktiv.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ==================== SUB-TAB: ÜBERSICHT ====================
function OverviewSection({ groups, children, authHeaders, authToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE}/api/analytics/overview`, authHeaders())
      .then(res => setData(res.data))
      .catch(err => console.error("Overview error:", err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-8 text-center text-slate-400">Lade Übersicht...</div>;
  if (!data) return null;

  const genderLabels = { m: "Jungen", w: "Mädchen", d: "Divers", unbekannt: "Nicht angegeben" };
  const totalGender = Object.values(data.gender || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-4" data-testid="overview-section">
      {/* Gesamt-Karten */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Users className="h-4 w-4" />
            <span className="text-xs font-medium">Kinder gesamt</span>
          </div>
          <div className="text-2xl font-bold text-slate-900" data-testid="total-children">{data.total_children}</div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <BarChart3 className="h-4 w-4" />
            <span className="text-xs font-medium">Gruppen</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">{data.groups?.length || 0}</div>
        </div>
        {Object.entries(data.gender || {}).filter(([k]) => k !== "unbekannt").map(([key, val]) => (
          <div key={key} className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
            <div className="text-xs font-medium text-slate-500 mb-1">{genderLabels[key] || key}</div>
            <div className="text-2xl font-bold text-slate-900">{val}</div>
          </div>
        ))}
      </div>

      {/* Altersverteilung */}
      {Object.keys(data.age_buckets || {}).length > 0 && (
        <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Altersverteilung</h3>
          <div className="flex items-end gap-2 h-32">
            {["0-4", "5-6", "7-8", "9-10", "11+"].map(bucket => {
              const val = data.age_buckets[bucket] || 0;
              const maxVal = Math.max(...Object.values(data.age_buckets), 1);
              const pct = (val / maxVal) * 100;
              return (
                <div key={bucket} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-semibold text-slate-700">{val}</span>
                  <div className="w-full bg-slate-100 rounded-t-md relative" style={{ height: "80px" }}>
                    <div
                      className="absolute bottom-0 w-full rounded-t-md bg-blue-500 transition-all"
                      style={{ height: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-slate-500">{bucket} J.</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gruppen-Tabelle */}
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Gruppen im Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="overview-groups-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Gruppe</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Kinder</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600">m / w</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Neu</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Alter (min-max)</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Alter Ø</th>
              </tr>
            </thead>
            <tbody>
              {data.groups?.map((g, idx) => (
                <tr key={g.group_id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: g.color }} />
                      <span className="font-medium text-slate-900">{g.group_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center font-semibold">{g.count}</td>
                  <td className="px-3 py-2.5 text-center text-slate-600">{g.gender?.m || 0} / {g.gender?.w || 0}</td>
                  <td className="px-3 py-2.5 text-center">
                    {g.new_count > 0 && <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">{g.new_count}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center text-slate-600">
                    {g.min_age != null && g.max_age != null ? `${g.min_age} - ${g.max_age}` : "-"}
                  </td>
                  <td className="px-3 py-2.5 text-center font-medium text-slate-700">
                    {g.avg_age != null ? `${g.avg_age} J.` : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ==================== SUB-TAB: LEISTUNGSSTAND ====================
function TrainingProgressSection({ groups, authHeaders, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupFilter, setGroupFilter] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const loadData = useCallback((gid) => {
    setLoading(true);
    const params = gid ? { group_id: gid } : {};
    axios.get(`${API_BASE}/api/analytics/training-progress`, { params, ...authHeaders() })
      .then(res => setData(res.data))
      .catch(() => toast?.({ title: "Fehler beim Laden", variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [authHeaders, toast]);

  useEffect(() => { loadData(groupFilter); }, [groupFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const getGroupStyle = useCallback((groupId) => {
    const group = (data?.groups || groups || []).find(g => g.id === groupId);
    if (!group) return { bg: "#e5e7eb", text: "#1f2937" };
    const bg = group.color || "#e5e7eb";
    let textColor = "#1f2937";
    try {
      if (bg.startsWith("#") && bg.length === 7) {
        const r = parseInt(bg.slice(1, 3), 16);
        const gv = parseInt(bg.slice(3, 5), 16);
        const b = parseInt(bg.slice(5, 7), 16);
        textColor = (0.299 * r + 0.587 * gv + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
      }
    } catch (e) {}
    return { bg, text: textColor };
  }, [data?.groups, groups]);

  return (
    <div className="space-y-4" data-testid="training-progress-section">
      {/* Gruppen-Filter */}
      <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Gruppe filtern</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setGroupFilter(null)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${!groupFilter ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="training-filter-all"
          >
            Alle
          </button>
          {(data?.groups || groups || []).map(g => {
            const style = getGroupStyle(g.id);
            return (
              <button
                key={g.id}
                onClick={() => setGroupFilter(groupFilter === g.id ? null : g.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${groupFilter === g.id ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                style={{ backgroundColor: style.bg, color: style.text }}
                data-testid={`training-filter-${g.id}`}
              >
                {g.name}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="p-8 text-center text-slate-400">Lade Leistungsstand...</div>}

      {/* Sektionen */}
      {data?.sections?.map(section => {
        const isExpanded = expandedSection === section.key;
        const pctCompleted = section.total_children > 0 ? Math.round(section.completed / section.total_children * 100) : 0;
        return (
          <div key={section.key} className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden" data-testid={`training-section-${section.key}`}>
            {/* Section Header */}
            <button
              onClick={() => setExpandedSection(isExpanded ? null : section.key)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="h-4 w-4 rounded" style={{ backgroundColor: section.color }} />
                <div className="text-left">
                  <h3 className="font-semibold text-slate-900">{section.name}</h3>
                  <p className="text-xs text-slate-500">{section.total_children} Kinder</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Mini-Statistik */}
                <div className="hidden sm:flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1 text-emerald-600 font-medium">
                    <Award className="h-3.5 w-3.5" /> {section.completed} fertig
                  </span>
                  <span className="text-amber-600 font-medium">{section.in_progress} in Arbeit</span>
                  <span className="text-slate-400">{section.not_started} offen</span>
                  {section.certificates > 0 && (
                    <span className="flex items-center gap-1 text-blue-600 font-medium">
                      {section.certificates} Zeugnisse
                    </span>
                  )}
                </div>
                {/* Progress bar */}
                <div className="flex items-center gap-2">
                  <div className="h-2 w-16 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pctCompleted}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-600 w-8">{pctCompleted}%</span>
                </div>
                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              </div>
            </button>

            {/* Expanded: Kinder-Tabelle */}
            {isExpanded && (
              <div className="border-t border-slate-100 overflow-x-auto">
                {/* Mobile: Mini-Statistik */}
                <div className="sm:hidden flex flex-wrap gap-2 p-3 border-b border-slate-50 text-xs">
                  <span className="text-emerald-600 font-medium">{section.completed} fertig</span>
                  <span className="text-amber-600 font-medium">{section.in_progress} in Arbeit</span>
                  <span className="text-slate-400">{section.not_started} offen</span>
                  {section.certificates > 0 && <span className="text-blue-600 font-medium">{section.certificates} Zeugnisse</span>}
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Gruppe</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Fortschritt</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Zeugnis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.children
                      ?.sort((a, b) => b.percentage - a.percentage)
                      .map((child, ci) => {
                        const gName = (data?.groups || []).find(g => g.id === child.group_id)?.name || "";
                        return (
                          <tr key={child.child_id} className={ci % 2 === 0 ? "bg-white" : "bg-slate-50/30"}>
                            <td className="px-4 py-2 font-medium text-slate-900">{child.first_name} {child.last_name}</td>
                            <td className="px-3 py-2 text-center text-xs text-slate-500">{gName}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-center gap-2">
                                <div className="h-1.5 w-12 rounded-full bg-slate-100 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${child.percentage === 100 ? "bg-emerald-500" : child.percentage > 0 ? "bg-amber-400" : "bg-slate-200"}`}
                                    style={{ width: `${child.percentage}%` }}
                                  />
                                </div>
                                <span className="text-xs text-slate-600 w-12 text-right">{child.exercises_done}/{child.total_exercises}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {child.has_certificate && <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">Ja</span>}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==================== SUB-TAB: ANWESENHEIT (bestehend) ====================
function AttendanceSection({ children, groups, authHeaders, authToken, toast, formatDateISOToGerman }) {
  const [groupFilter, setGroupFilter] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tableData, setTableData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const getGroupStyle = useCallback((groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return { bg: "#e5e7eb", text: "#1f2937" };
    const bg = group.color || "#e5e7eb";
    let textColor = "#1f2937";
    try {
      if (bg.startsWith("#") && bg.length === 7) {
        const r = parseInt(bg.slice(1, 3), 16);
        const g = parseInt(bg.slice(3, 5), 16);
        const b = parseInt(bg.slice(5, 7), 16);
        textColor = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
      }
    } catch (e) {}
    return { bg, text: textColor };
  }, [groups]);

  const filteredChildren = useMemo(() => {
    if (!groupFilter) return [];
    return (children || [])
      .filter((c) => c.group_id === groupFilter && !c.archived && c.status !== "storniert")
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [children, groupFilter]);

  const loadTableData = useCallback(async (page = 1) => {
    if (!groupFilter) {
      toast({ title: "Bitte wähle zuerst eine Gruppe aus", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const params = { group_id: groupFilter, page, page_size: pageSize };
      if (dateFrom) params.from_date = dateFrom;
      if (dateTo) params.to_date = dateTo;
      if (selectedChildId) params.child_id = selectedChildId;
      const res = await axios.get(`${API_BASE}/api/attendance/table`, { params, ...authHeaders() });
      setTableData(res.data);
      setCurrentPage(page);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden der Auswertung", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [groupFilter, dateFrom, dateTo, selectedChildId, authHeaders, toast]);

  useEffect(() => {
    setTableData(null);
    setSelectedChildId(null);
    setCurrentPage(1);
  }, [groupFilter]);

  const renderStatusIcon = (status) => {
    if (status === "anwesend") return <img src={iconPresentActive} alt="anwesend" className="h-5 w-5 mx-auto" />;
    if (status === "fehlt") return <img src={iconAbsentActive} alt="fehlt" className="h-5 w-5 mx-auto" />;
    if (status === "entschuldigt") return <img src={iconExcusedActive} alt="entschuldigt" className="h-5 w-5 mx-auto" />;
    return <span className="inline-block h-4 w-4 rounded-full bg-slate-200 mx-auto" />;
  };

  const exportPDF = async () => {
    if (!tableData || !groupFilter) {
      toast({ title: "Bitte zuerst eine Auswertung laden", variant: "destructive" });
      return;
    }
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const group = groups?.find((g) => g.id === groupFilter);
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.src = "/fitmacher_logo.png";
        await new Promise((resolve, reject) => { logoImg.onload = resolve; logoImg.onerror = reject; setTimeout(reject, 2000); });
        doc.addImage(logoImg, "PNG", 10, 8, 30, 15);
      } catch (e) {}
      let y = 15;
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(`Anwesenheits-Auswertung: ${group?.name || "Gruppe"}`, pageWidth / 2, y, { align: "center" });
      y += 8;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const zeitraum = dateFrom || dateTo
        ? `Zeitraum: ${dateFrom ? formatDateISOToGerman(dateFrom) : "Anfang"} - ${dateTo ? formatDateISOToGerman(dateTo) : "Heute"}`
        : "Zeitraum: Alle Termine";
      doc.text(zeitraum, pageWidth / 2, y, { align: "center" });
      y += 5;
      doc.text(`Erstellt am: ${new Date().toLocaleDateString("de-DE")}`, pageWidth / 2, y, { align: "center" });
      y += 10;
      if (tableData.summary) {
        doc.setFontSize(9);
        doc.text(`Kinder: ${tableData.summary.total_children} | Termine im Zeitraum: ${tableData.summary.sessions_in_range}`, 14, y);
        y += 5;
        doc.setFontSize(8);
        doc.text("Legende: A = Anwesend | F = Gefehlt | E = Entschuldigt | - = Keine Eintragung", 14, y);
        y += 8;
      }
      let allData = tableData;
      if (tableData.pagination.total_pages > 1) {
        const params = { group_id: groupFilter, page: 1, page_size: 1000 };
        if (dateFrom) params.from_date = dateFrom;
        if (dateTo) params.to_date = dateTo;
        if (selectedChildId) params.child_id = selectedChildId;
        const fullRes = await axios.get(`${API_BASE}/api/attendance/table`, { params, ...authHeaders() });
        allData = fullRes.data;
      }
      const headers = ["Name", ...allData.columns.map(c => formatDateISOToGerman(c.date).split(".").slice(0, 2).join(".")), "Anw.", "Fehl", "Ent.", "%"];
      const body = allData.rows.map(row => [
        `${row.first_name} ${row.last_name}`,
        ...allData.columns.map(col => { const att = row.attendance[col.id]; if (!att) return "-"; if (att.status === "anwesend") return "A"; if (att.status === "fehlt") return "F"; if (att.status === "entschuldigt") return "E"; return "-"; }),
        row.stats.present.toString(), row.stats.absent.toString(), row.stats.excused.toString(),
        row.stats.total > 0 ? `${Math.round((row.stats.present / row.stats.total) * 100)}%` : "-"
      ]);
      autoTable(doc, {
        startY: y, head: [headers], body,
        styles: { fontSize: 7, cellPadding: 1.5, halign: "center" },
        headStyles: { fillColor: [100, 100, 100], textColor: 255, fontStyle: "bold", fontSize: 7 },
        columnStyles: { 0: { halign: "left", cellWidth: 40 } },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        didDrawPage: (d) => { doc.setFontSize(8); doc.text(`Seite ${d.pageNumber}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" }); },
      });
      const filename = selectedChildId
        ? `Anwesenheit_${allData.rows[0]?.first_name}_${allData.rows[0]?.last_name}.pdf`
        : `Anwesenheit_${group?.name || "Gruppe"}_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(filename);
      toast({ title: "PDF erfolgreich erstellt" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Erstellen der PDF", variant: "destructive" });
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= (tableData?.pagination?.total_pages || 1)) loadTableData(page);
  };

  return (
    <div className="space-y-4" data-testid="attendance-section">
      <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
        {/* Gruppen-Filter */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-slate-600">Gruppe auswählen</label>
          <div className="flex flex-wrap gap-2">
            {groups?.map((g) => {
              const style = getGroupStyle(g.id);
              const count = (children || []).filter((c) => c.group_id === g.id && !c.archived && c.status !== "storniert").length;
              const isSelected = groupFilter === g.id;
              return (
                <button key={g.id} onClick={() => setGroupFilter(isSelected ? null : g.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${isSelected ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                  style={{ backgroundColor: style.bg, color: style.text }} data-testid={`att-group-filter-${g.id}`}
                >{g.name} ({count})</button>
              );
            })}
          </div>
        </div>

        {/* Kind-Auswahl */}
        {groupFilter && (
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-slate-600">Kind auswählen (optional)</label>
            <select value={selectedChildId || ""} onChange={(e) => setSelectedChildId(e.target.value || null)}
              className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="att-child-select"
            >
              <option value="">– Gesamte Gruppe –</option>
              {filteredChildren.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
        )}

        {/* Zeitraum */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Von</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="att-date-from" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Bis</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="att-date-to" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => loadTableData(1)} disabled={isLoading || !groupFilter}
            className="rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            data-testid="att-load-btn"
          >{isLoading ? "Lädt..." : "Auswertung laden"}</button>
          {tableData && (
            <button onClick={exportPDF}
              className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-200"
              data-testid="att-export-pdf-btn"
            >PDF exportieren</button>
          )}
        </div>
      </div>

      {/* Tabelle */}
      {tableData && (
        <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
          <div className="border-b border-slate-100 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">
                  {groups?.find((g) => g.id === groupFilter)?.name || "Gruppe"}
                  {selectedChildId && tableData.rows[0] && (
                    <span className="ml-2 text-slate-500">– {tableData.rows[0].first_name} {tableData.rows[0].last_name}</span>
                  )}
                </h3>
                <p className="text-sm text-slate-500">
                  {tableData.summary.total_children} Kind{tableData.summary.total_children !== 1 ? "er" : ""} · {tableData.summary.sessions_in_range} Termin{tableData.summary.sessions_in_range !== 1 ? "e" : ""}
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1"><img src={iconPresentActive} alt="" className="h-4 w-4" /><span className="text-slate-600">Anwesend</span></div>
                <div className="flex items-center gap-1"><img src={iconAbsentActive} alt="" className="h-4 w-4" /><span className="text-slate-600">Gefehlt</span></div>
                <div className="flex items-center gap-1"><img src={iconExcusedActive} alt="" className="h-4 w-4" /><span className="text-slate-600">Entschuldigt</span></div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="att-evaluation-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-700 min-w-[180px] z-10">Name</th>
                  {tableData.columns.map((col) => (
                    <th key={col.id} className="px-2 py-3 text-center font-medium text-slate-600 min-w-[60px]">
                      <div className="text-xs">{col.weekday?.slice(0, 2)}</div>
                      <div>{formatDateISOToGerman(col.date).split(".").slice(0, 2).join(".")}</div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center font-semibold text-emerald-700 bg-emerald-50 min-w-[50px]">Anw.</th>
                  <th className="px-3 py-3 text-center font-semibold text-rose-700 bg-rose-50 min-w-[50px]">Fehl</th>
                  <th className="px-3 py-3 text-center font-semibold text-amber-700 bg-amber-50 min-w-[50px]">Ent.</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-700 bg-slate-100 min-w-[50px]">%</th>
                </tr>
              </thead>
              <tbody>
                {tableData.rows.map((row, idx) => {
                  const percentage = row.stats.total > 0 ? Math.round((row.stats.present / row.stats.total) * 100) : 0;
                  return (
                    <tr key={row.child_id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                      <td className="sticky left-0 bg-inherit px-4 py-2.5 font-medium text-slate-900 z-10">
                        <div className="flex items-center gap-2">
                          {row.has_photo ? (
                            <img src={row.photo_thumb_url || `${API_BASE}/api/children/${row.child_id}/photo?token=${authToken}&size=thumb`}
                              alt="" className="h-7 w-7 rounded-full object-cover" loading="lazy" />
                          ) : (
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
                              {row.first_name?.[0]}{row.last_name?.[0]}
                            </div>
                          )}
                          <span>{row.first_name} {row.last_name}</span>
                        </div>
                      </td>
                      {tableData.columns.map((col) => {
                        const att = row.attendance[col.id];
                        return <td key={col.id} className="px-2 py-2.5 text-center">{renderStatusIcon(att?.status)}</td>;
                      })}
                      <td className="px-3 py-2.5 text-center font-semibold text-emerald-700 bg-emerald-50/50">{row.stats.present}</td>
                      <td className="px-3 py-2.5 text-center font-semibold text-rose-700 bg-rose-50/50">{row.stats.absent}</td>
                      <td className="px-3 py-2.5 text-center font-semibold text-amber-700 bg-amber-50/50">{row.stats.excused}</td>
                      <td className="px-3 py-2.5 text-center font-semibold text-slate-700 bg-slate-100/50">
                        <div className="flex items-center justify-center gap-1">
                          <div className="h-1.5 w-8 rounded-full bg-slate-200 overflow-hidden" title={`${percentage}%`}>
                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${percentage}%` }} />
                          </div>
                          <span className="text-xs">{percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {tableData.pagination.total_pages > 1 && (
            <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-slate-500">
                Seite {tableData.pagination.page} von {tableData.pagination.total_pages}
                <span className="ml-2 text-slate-400">({tableData.pagination.total_sessions} Termine gesamt)</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1 || isLoading}
                  className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50" data-testid="att-prev-page-btn">
                  <ChevronRight className="h-4 w-4 rotate-180" /> Vorherige 10
                </button>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= tableData.pagination.total_pages || isLoading}
                  className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50" data-testid="att-next-page-btn">
                  Nächste 10 <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!tableData && !isLoading && (
        <div className="rounded-xl bg-slate-50 p-8 text-center border border-slate-100">
          <Calendar className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <h3 className="text-base font-semibold text-slate-700 mb-1">Keine Auswertung geladen</h3>
          <p className="text-sm text-slate-500">Wähle eine Gruppe aus und klicke auf "Auswertung laden".</p>
        </div>
      )}
    </div>
  );
}

// ==================== SUB-TAB: EXPORT ====================
function ExportSection({ groups, authHeaders, authToken, toast, formatDateISOToGerman }) {
  const [exportType, setExportType] = useState("full");
  const [format, setFormat] = useState("excel");
  const [groupFilter, setGroupFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Schnellauswahl-Zeiträume
  const setQuickRange = (range) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (range) {
      case "month":
        setDateFrom(new Date(y, m, 1).toISOString().split("T")[0]);
        setDateTo(new Date(y, m + 1, 0).toISOString().split("T")[0]);
        break;
      case "quarter": {
        const qStart = Math.floor(m / 3) * 3;
        setDateFrom(new Date(y, qStart, 1).toISOString().split("T")[0]);
        setDateTo(new Date(y, qStart + 3, 0).toISOString().split("T")[0]);
        break;
      }
      case "year":
        setDateFrom(new Date(y, 0, 1).toISOString().split("T")[0]);
        setDateTo(new Date(y, 11, 31).toISOString().split("T")[0]);
        break;
      case "all":
        setDateFrom("");
        setDateTo("");
        break;
      default:
        break;
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      params.append("export_type", exportType);
      if (groupFilter) params.append("group_id", groupFilter);
      if (dateFrom) params.append("from_date", dateFrom);
      if (dateTo) params.append("to_date", dateTo);

      const endpoint = format === "excel" ? "excel" : "pdf";
      const response = await axios.get(`${API_BASE}/api/analytics/export/${endpoint}?${params.toString()}`, {
        ...authHeaders(),
        responseType: "blob",
      });

      // Download triggern
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      let filename = `Export.${format === "excel" ? "xlsx" : "pdf"}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `${format === "excel" ? "Excel" : "PDF"} erfolgreich heruntergeladen` });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Export", variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  const exportOptions = [
    { value: "full", label: "Gesamtbericht", desc: "Kinderliste + Leistungsstand + Anwesenheit", icon: BarChart3 },
    { value: "children", label: "Kinderliste", desc: "Name, Gruppe, Alter, Kontaktdaten", icon: Users },
    { value: "training", label: "Leistungsstand", desc: "Fortschritt aller Trainingsabschnitte", icon: Award },
    { value: "attendance", label: "Anwesenheit", desc: "Anwesenheitstabelle mit Statistiken", icon: Calendar },
  ];

  return (
    <div className="space-y-4" data-testid="export-section">
      {/* Export-Typ */}
      <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Was exportieren?</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {exportOptions.map(opt => {
            const Icon = opt.icon;
            const isSelected = exportType === opt.value;
            return (
              <button key={opt.value} onClick={() => setExportType(opt.value)}
                className={`flex items-start gap-3 rounded-lg p-3 text-left transition-all border ${isSelected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                data-testid={`export-type-${opt.value}`}
              >
                <Icon className={`h-5 w-5 mt-0.5 ${isSelected ? "text-blue-600" : "text-slate-400"}`} />
                <div>
                  <div className={`text-sm font-medium ${isSelected ? "text-blue-900" : "text-slate-700"}`}>{opt.label}</div>
                  <div className="text-xs text-slate-500">{opt.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Format */}
      <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Format</h3>
        <div className="flex gap-2">
          <button onClick={() => setFormat("excel")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all border ${format === "excel" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
            data-testid="export-format-excel"
          >
            <FileSpreadsheet className="h-4 w-4" /> Excel (.xlsx)
          </button>
          <button onClick={() => setFormat("pdf")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all border ${format === "pdf" ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
            data-testid="export-format-pdf"
          >
            <FileText className="h-4 w-4" /> PDF (.pdf)
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Filter (optional)</h3>
        
        {/* Gruppe */}
        <div className="mb-3">
          <label className="mb-1 block text-xs text-slate-500">Gruppe</label>
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
            className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="export-group-filter"
          >
            <option value="">Alle Gruppen</option>
            {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>

        {/* Zeitraum (nur für Anwesenheit relevant) */}
        {(exportType === "attendance" || exportType === "full") && (
          <>
            <div className="mb-2">
              <label className="mb-1 block text-xs text-slate-500">Zeitraum</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { key: "month", label: "Dieser Monat" },
                  { key: "quarter", label: "Dieses Quartal" },
                  { key: "year", label: "Dieses Jahr" },
                  { key: "all", label: "Alles" },
                ].map(p => (
                  <button key={p.key} onClick={() => setQuickRange(p.key)}
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                    data-testid={`export-range-${p.key}`}
                  >{p.label}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Von</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="export-date-from" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Bis</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="export-date-to" />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Export Button */}
      <button onClick={handleExport} disabled={isExporting}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 transition-colors shadow-sm"
        data-testid="export-download-btn"
      >
        <Download className="h-4 w-4" />
        {isExporting ? "Wird erstellt..." : `${format === "excel" ? "Excel" : "PDF"} herunterladen`}
      </button>
    </div>
  );
}


// ==================== SUB-TAB: RANGLISTE ====================
function RankingSection({ groups, authHeaders, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const authRef = React.useRef(authHeaders);
  authRef.current = authHeaders;

  const availableYears = [];
  for (let y = 2026; y <= currentYear; y++) availableYears.push(y);

  useEffect(() => {
    setLoading(true);
    const params = { year: selectedYear };
    if (selectedGroup) params.group_id = selectedGroup;
    axios.get(`${API_BASE}/api/analytics/attendance-ranking`, {
      params,
      headers: authRef.current()?.headers || {},
    })
      .then(res => setData(res.data))
      .catch(() => toast?.({ title: "Fehler beim Laden der Anwesenheitsquote", variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [selectedYear, selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data && loading) return <div className="p-8 text-center text-slate-400">Lade Anwesenheitsquote...</div>;
  if (!data || !data.ranking?.length) return (
    <div className="rounded-xl bg-slate-50 p-8 text-center border border-slate-100">
      <Trophy className="h-10 w-10 mx-auto mb-3 text-slate-300" />
      <h3 className="text-base font-semibold text-slate-700 mb-1">Keine Anwesenheitsdaten</h3>
      <p className="text-sm text-slate-500">Es wurden noch keine Anwesenheiten dokumentiert.</p>
    </div>
  );

  const getMedalStyle = (rank) => {
    if (rank === 1) return "bg-amber-400 text-white";
    if (rank === 2) return "bg-slate-300 text-white";
    if (rank === 3) return "bg-amber-600 text-white";
    return "bg-slate-100 text-slate-500";
  };

  const getRateColor = (rate) => {
    if (rate >= 90) return "text-emerald-600";
    if (rate >= 70) return "text-blue-600";
    if (rate >= 50) return "text-amber-600";
    return "text-rose-600";
  };

  const getRateBarColor = (rate) => {
    if (rate >= 90) return "bg-emerald-500";
    if (rate >= 70) return "bg-blue-500";
    if (rate >= 50) return "bg-amber-400";
    return "bg-rose-400";
  };

  return (
    <div className="space-y-4" data-testid="ranking-section">
      <div className="rounded-xl bg-white p-4 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-700">Anwesenheitsquote</h3>
          <span className="text-xs text-slate-400">{data.total_sessions} Termine ausgewertet</span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">{data.ranking.length} Kinder · {selectedGroup ? (groups || []).find(g => g.id === selectedGroup)?.name || "Gruppe" : "Alle Gruppen"} · Sortiert nach Quote</p>
          <div className="flex items-center gap-1.5">
            {availableYears.map(y => (
              <button
                key={y}
                onClick={() => setSelectedYear(y)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${selectedYear === y ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`ranking-year-${y}`}
              >{y}</button>
            ))}
          </div>
        </div>
        {/* Gruppenfilter */}
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
          <button
            onClick={() => setSelectedGroup(null)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${!selectedGroup ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="ranking-group-all"
          >Alle</button>
          {(groups || []).map(g => {
            const isActive = selectedGroup === g.id;
            return (
              <button key={g.id}
                onClick={() => setSelectedGroup(isActive ? null : g.id)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${isActive ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                style={{ backgroundColor: g.color || "#e5e7eb", color: g.color && g.color !== "#e5e7eb" ? "#fff" : "#374151" }}
                data-testid={`ranking-group-${g.id}`}
              >{g.name}</button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ranking-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600 w-12">#</th>
                <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Name</th>
                <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Gruppe</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600 hidden sm:table-cell">Anw.</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600 hidden sm:table-cell">Fehl</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600 hidden sm:table-cell">Ent.</th>
                <th className="px-3 py-2.5 text-center font-semibold text-slate-600 hidden sm:table-cell">Termine</th>
                <th className="px-3 py-2.5 text-right font-semibold text-slate-600 min-w-[120px]">Quote</th>
              </tr>
            </thead>
            <tbody>
              {data.ranking.map((r, idx) => (
                <tr key={r.child_id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${getMedalStyle(r.rank)}`}>
                      {r.rank}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{r.name}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{ backgroundColor: r.group_color + "20", color: r.group_color !== "#e5e7eb" ? r.group_color : "#374151" }}>
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.group_color }} />
                      {r.group_name}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center text-emerald-600 font-medium hidden sm:table-cell">{r.present}</td>
                  <td className="px-3 py-2.5 text-center text-rose-600 font-medium hidden sm:table-cell">{r.absent}</td>
                  <td className="px-3 py-2.5 text-center text-amber-600 font-medium hidden sm:table-cell">{r.excused}</td>
                  <td className="px-3 py-2.5 text-center text-slate-500 hidden sm:table-cell">{r.total}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden hidden sm:block">
                        <div className={`h-full rounded-full transition-all ${getRateBarColor(r.rate)}`} style={{ width: `${r.rate}%` }} />
                      </div>
                      <span className={`text-sm font-bold ${getRateColor(r.rate)} w-12 text-right`}>{r.rate}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ==================== HAUPTKOMPONENTE ====================
export function EvaluationTabModern({
  children,
  groups,
  authHeaders,
  authToken,
  toast,
  formatDateISOToGerman,
}) {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { key: "overview", label: "Übersicht", icon: BarChart3 },
    { key: "training", label: "Leistungsstand", icon: TrendingUp },
    { key: "attendance", label: "Anwesenheit", icon: Calendar },
    { key: "ranking", label: "Anwesenheitsquote", icon: Trophy },
    { key: "export", label: "Export", icon: Download },
  ];

  return (
    <div className="space-y-4" data-testid="evaluation-tab">
      {/* Sub-Tab Navigation */}
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-1.5">
        <div className="flex gap-1 overflow-x-auto">
          {sections.map(s => {
            const Icon = s.icon;
            const isActive = activeSection === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-all ${
                  isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`eval-tab-${s.key}`}
              >
                <Icon className="h-4 w-4" />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {activeSection === "overview" && (
        <OverviewSection groups={groups} children={children} authHeaders={authHeaders} authToken={authToken} />
      )}
      {activeSection === "ranking" && (
        <RankingSection groups={groups} authHeaders={authHeaders} toast={toast} />
      )}
      {activeSection === "training" && (
        <TrainingProgressSection groups={groups} authHeaders={authHeaders} toast={toast} />
      )}
      {activeSection === "attendance" && (
        <AttendanceSection children={children} groups={groups} authHeaders={authHeaders} authToken={authToken} toast={toast} formatDateISOToGerman={formatDateISOToGerman} />
      )}
      {activeSection === "export" && (
        <ExportSection groups={groups} authHeaders={authHeaders} authToken={authToken} toast={toast} formatDateISOToGerman={formatDateISOToGerman} />
      )}
    </div>
  );
}
